import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { Shield, Heart, Sword, Edit2, Trash2, Crown, Users, ShoppingBag, User, Handshake, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const TYPE_CONFIG = {
  boss: { icon: Crown, color: 'text-red-500', bg: 'from-red-950/50', border: 'border-red-900' },
  enemy: { icon: Sword, color: 'text-red-400', bg: 'from-red-900/30', border: 'border-red-800/50' },
  friendly: { icon: Handshake, color: 'text-green-500', bg: 'from-green-950/30', border: 'border-green-900/50' },
  neutral: { icon: HelpCircle, color: 'text-stone-400', bg: 'from-stone-800/30', border: 'border-stone-700' },
  npc: { icon: User, color: 'text-amber-500', bg: 'from-amber-950/30', border: 'border-amber-900/50' },
  merchant: { icon: ShoppingBag, color: 'text-yellow-500', bg: 'from-yellow-950/30', border: 'border-yellow-900/50' },
  companion: { icon: Users, color: 'text-blue-500', bg: 'from-blue-950/30', border: 'border-blue-900/50' },
};

export default function CharacterCard({ character, viewMode = 'grid', onClick, onEdit, onDelete, isDragging }) {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: character.id,
  });

  const style = transform ? {
    transform: CSS.Translate.toString(transform),
  } : undefined;

  const config = TYPE_CONFIG[character.type] || TYPE_CONFIG.npc;
  const TypeIcon = config.icon;

  const hpPercent = character.stats?.hp?.max 
    ? (character.stats.hp.current / character.stats.hp.max) * 100 
    : 100;

  const hpClass = hpPercent <= 25 ? 'bg-red-600' : hpPercent <= 50 ? 'bg-yellow-600' : 'bg-green-600';

  if (viewMode === 'list') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        {...listeners}
        {...attributes}
        className={`character-card type-${character.type} flex items-center gap-4 p-3 bg-stone-900 border ${config.border} rounded-lg cursor-grab hover:border-amber-700 transition-all ${isDragging ? 'opacity-50' : ''}`}
        data-testid={`character-card-${character.id}`}
      >
        {character.image ? (
          <img src={character.image} alt={character.name} className="w-12 h-12 rounded object-cover border border-stone-700" />
        ) : (
          <div className="w-12 h-12 rounded bg-stone-800 border border-stone-700 flex items-center justify-center">
            <TypeIcon className={`w-6 h-6 ${config.color}`} />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-cinzel font-bold text-stone-100 truncate">{character.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded capitalize ${config.color} bg-stone-800`}>
              {character.type}
            </span>
          </div>
          {character.description && (
            <p className="text-sm text-stone-400 truncate">{character.description}</p>
          )}
        </div>

        <div className="flex items-center gap-4">
          {character.stats?.hp?.max && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1 text-stone-300">
                    <Heart className="w-4 h-4 text-red-500" />
                    <span className="font-cinzel text-sm">{character.stats.hp.current}/{character.stats.hp.max}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Hit Points</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {character.stats?.ac && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1 text-stone-300">
                    <Shield className="w-4 h-4 text-blue-500" />
                    <span className="font-cinzel text-sm">{character.stats.ac}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Armor Class</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onClick?.(); }}
            className="h-8 w-8 text-stone-400 hover:text-amber-500"
            data-testid={`view-character-${character.id}`}
          >
            <User className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
            className="h-8 w-8 text-stone-400 hover:text-amber-500"
            data-testid={`edit-character-${character.id}`}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
            className="h-8 w-8 text-stone-400 hover:text-red-500"
            data-testid={`delete-character-${character.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      whileHover={{ scale: 1.02, y: -4 }}
      transition={{ duration: 0.2 }}
      className={`character-card type-${character.type} bg-gradient-to-b ${config.bg} to-stone-900 border ${config.border} rounded-lg overflow-hidden cursor-grab ${isDragging ? 'opacity-50 scale-105' : ''}`}
      data-testid={`character-card-${character.id}`}
    >
      {/* Image or placeholder */}
      <div className="aspect-[4/3] relative overflow-hidden bg-stone-800">
        {character.image ? (
          <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TypeIcon className={`w-16 h-16 ${config.color} opacity-50`} />
          </div>
        )}
        {/* Type badge */}
        <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-cinzel uppercase ${config.color} bg-black/70 backdrop-blur`}>
          {character.type}
        </div>
        {/* HP Bar overlay */}
        {character.stats?.hp?.max && (
          <div className="absolute bottom-0 left-0 right-0 h-2 bg-black/50">
            <div className={`h-full ${hpClass} transition-all`} style={{ width: `${hpPercent}%` }} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-cinzel font-bold text-lg text-stone-100 leading-tight">{character.name}</h3>
          {character.alignment && (
            <span className="text-xs text-stone-500 uppercase tracking-wider">{character.alignment}</span>
          )}
        </div>

        {/* Quick stats */}
        <div className="flex gap-3">
          {character.stats?.hp?.max && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1 px-2 py-1 bg-stone-800 rounded text-sm">
                    <Heart className="w-3 h-3 text-red-500" />
                    <span className="font-cinzel text-stone-200">{character.stats.hp.current}/{character.stats.hp.max}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Hit Points</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {character.stats?.ac && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="flex items-center gap-1 px-2 py-1 bg-stone-800 rounded text-sm">
                    <Shield className="w-3 h-3 text-blue-500" />
                    <span className="font-cinzel text-stone-200">{character.stats.ac}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Armor Class</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {/* Tags from traits */}
        {character.traits?.tags && (
          <div className="flex flex-wrap gap-1">
            {(Array.isArray(character.traits.tags) ? character.traits.tags : [character.traits.tags]).slice(0, 3).map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-stone-800 rounded text-xs text-stone-400">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t border-stone-800">
          <Button
            variant="ghost"
            size="sm"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick?.(); }}
            className="flex-1 text-stone-400 hover:text-amber-500 hover:bg-stone-800"
            data-testid={`view-btn-${character.id}`}
          >
            View
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEdit?.(); }}
            className="text-stone-400 hover:text-amber-500 hover:bg-stone-800"
            data-testid={`edit-btn-${character.id}`}
          >
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onDelete?.(); }}
            className="text-stone-400 hover:text-red-500 hover:bg-stone-800"
            data-testid={`delete-btn-${character.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
}
